/**
 * useDashboard — load a single saved dashboard for the editor host.
 *
 * `GET /api/dashboards/:id` → unwraps the `{ data }` envelope and returns the
 * dashboard (id, name, clientId, canvasLayout, …). Mirrors the report host's
 * `["report", reportId]` query (apps/web/src/routes/_authenticated/reports/
 * $reportId.edit.tsx) but for the dashboards CRUD. Gated on `enabled` so a
 * brand-new dashboard (no id yet) never fires it.
 */

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { apiGet } from "../lib/apiClient";
import type { DashboardCanvasLayout } from "../lib/dashboardLayout";

/** A resolved roll-up member (the dashboard's brand/portfolio clients). */
export interface DashboardMemberSummary {
  id: string;
  name: string;
}

/** Shape returned by `GET /api/dashboards/:id` (the `.data` payload). */
export interface LoadedDashboard {
  id: string;
  /** Null for a ROLL-UP dashboard (kind='rollup'); the client id otherwise. */
  clientId: string | null;
  name: string;
  /**
   * Story 40-7 — roll-up discriminator + resolved members. A single-client
   * dashboard returns kind='client' (or undefined on a legacy row), a null
   * rollupScope/brandId, and an EMPTY member set. A roll-up returns the
   * server-resolved CURRENT members (brand=dynamic active clients,
   * portfolio=junction). Members are IMMUTABLE post-create (the editor reads
   * them; only the layout is mutable).
   */
  kind?: "client" | "rollup";
  rollupScope?: "brand" | "portfolio" | null;
  brandId?: string | null;
  members?: DashboardMemberSummary[];
  memberClientIds?: string[];
  /** The versioned canvas blob (may be an older/looser shape — parse defensively). */
  canvasLayout: DashboardCanvasLayout | Record<string, unknown> | null;
  /** Publish state — "draft" | "ready" (text NOT NULL default 'draft' server-side). */
  status: string;
  /** Persisted primary range start (ISO "YYYY-MM-DD"), null until a range is saved. */
  dateRangeStart: string | null;
  /** Persisted primary range end (ISO "YYYY-MM-DD"), null until a range is saved. */
  dateRangeEnd: string | null;
  /** Persisted preset key (e.g. "last-30" or "custom"), null until a range is saved. */
  dateRangePreset: string | null;
  createdBy?: string | null;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
}

export function useDashboard(
  dashboardId: string | undefined,
  options?: { enabled?: boolean },
): UseQueryResult<LoadedDashboard> {
  const enabled = (options?.enabled ?? true) && !!dashboardId;
  return useQuery<LoadedDashboard>({
    queryKey: ["dashboard", dashboardId],
    queryFn: async () => {
      const res = await apiGet(`/api/dashboards/${dashboardId}`);
      if (!res.ok) throw new Error("Failed to load dashboard");
      const json = await res.json();
      return json.data as LoadedDashboard;
    },
    enabled,
  });
}
